import { useEffect, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import { validateImageFile } from '../utils/imageValidation';
import {
  getPresignedUploadUrl,
  confirmPhotoUpload,
  deleteAquariumPhoto,
} from '../services/aquariumPhotoService';

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export type UploadState =
  | { status: 'idle' }
  | { status: 'compressing' }
  | { status: 'uploading'; progress: number }
  | { status: 'success'; previewUrl: string; imageUrl: string; imageKey: string }
  | { status: 'error'; message: string };

export interface UsePhotoUploadReturn {
  state: UploadState;
  upload: (file: File, aquariumId: string) => Promise<void>;
  deletePhoto: (aquariumId: string) => Promise<void>;
  reset: () => void;
}

const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
} as const;

export function usePhotoUpload(): UsePhotoUploadReturn {
  const [state, setState] = useState<UploadState>({ status: 'idle' });
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (xhrRef.current) {
        xhrRef.current.abort();
        xhrRef.current = null;
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  function revokePreviewUrl(): void {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }

  function reset(): void {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    revokePreviewUrl();
    setState({ status: 'idle' });
  }

  async function upload(file: File, aquariumId: string): Promise<void> {
    // Revoke any previous preview URL before starting a new upload
    revokePreviewUrl();

    // Step 1: Validate
    const validation = await validateImageFile(file);
    if (!validation.valid) {
      setState({ status: 'error', message: validation.error ?? 'Validation failed.' });
      return;
    }

    setState({ status: 'compressing' });

    // Step 2: Compress
    let compressed: File;
    try {
      compressed = await imageCompression(file, COMPRESSION_OPTIONS);
    } catch {
      setState({ status: 'error', message: 'Image compression failed. Please try again.' });
      return;
    }

    // Step 3: Get presigned URL
    let uploadUrl: string;
    let objectKey: string;
    try {
      const result = await getPresignedUploadUrl(aquariumId, compressed.type);
      uploadUrl = result.uploadUrl;
      objectKey = result.objectKey;
    } catch {
      setState({ status: 'error', message: 'Failed to get upload URL. Please try again.' });
      return;
    }

    // Step 4: PUT to S3 via XHR (no Authorization header on presigned URLs)
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhrRef.current = xhr;

        xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
          if (event.lengthComputable) {
            const pct = Math.round((event.loaded / event.total) * 100);
            setState({ status: 'uploading', progress: pct });
          }
        });

        xhr.addEventListener('load', () => {
          xhrRef.current = null;
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`S3 upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          xhrRef.current = null;
          reject(new Error('Network error during S3 upload'));
        });

        xhr.addEventListener('abort', () => {
          xhrRef.current = null;
          reject(new Error('Upload was aborted'));
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', compressed.type);
        xhr.send(compressed);
      });
    } catch (err: unknown) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'S3 upload failed. Please try again.',
      });
      return;
    }

    // Step 5: Confirm with backend
    try {
      const confirmation = await confirmPhotoUpload(aquariumId, objectKey);
      const previewUrl = URL.createObjectURL(compressed);
      previewUrlRef.current = previewUrl;
      setState({
        status: 'success',
        previewUrl,
        imageUrl: confirmation.imageUrl,
        imageKey: objectKey,
      });
    } catch {
      setState({
        status: 'error',
        message: 'Upload succeeded but confirmation failed. Please refresh.',
      });
    }
  }

  async function deletePhoto(aquariumId: string): Promise<void> {
    try {
      await deleteAquariumPhoto(aquariumId);
      revokePreviewUrl();
      setState({ status: 'idle' });
    } catch {
      setState({ status: 'error', message: 'Failed to delete photo. Please try again.' });
    }
  }

  return { state, upload, deletePhoto, reset };
}
